import { cn } from "@newxsystems/shared";
import { StratosCardLink } from "../../../../modules/content/components/StratosCardLink/StratosCardLink";
import { StratosCommonContent } from "../../../../modules/content/components/StratosCommonContent/StratosCommonContent";
import { StratosMedia } from "../../../../modules/content/components/StratosMedia/StratosMedia";
import { StratosRichText } from "../../../../modules/content/components/StratosRichText/StratosRichText";
import { StratosSection } from "../../../../modules/content/components/StratosSection/StratosSection";
import type { ITestimonialGridBlockProps } from "./TestimonialGridBlock.props";

const columns = {
  1: "lg:grid-cols-1",
  2: "lg:grid-cols-2",
  3: "lg:grid-cols-3",
  4: "lg:grid-cols-4",
} as const;
const spans = {
  1: "lg:col-span-1",
  2: "lg:col-span-2",
  3: "lg:col-span-3",
  4: "lg:col-span-4",
} as const;

const surfaces = [
  "bg-deep-violet text-cloud-white",
  "bg-sunlit text-night-flight",
  "bg-horizon-blue text-night-flight",
] as const;

export const TestimonialGridBlock = ({
  alignment,
  anchorId,
  buttons,
  columnCount,
  description,
  eyebrow,
  heading,
  items,
  parentClass,
  spacing,
}: ITestimonialGridBlockProps) => {
  if (!items?.length) return null;

  return (
    <StratosSection className={parentClass} id={anchorId} spacing={spacing}>
      <div className="flex flex-col gap-10 md:gap-14">
        <StratosCommonContent
          alignment={alignment}
          buttons={buttons}
          description={description}
          eyebrow={eyebrow}
          heading={heading}
        />
        <div className={cn("grid grid-cols-1 gap-4 sm:grid-cols-2", columns[columnCount || 3])}>
          {items.map((item, index) => (
            <StratosCardLink
              className={cn(
                "flex min-h-72 flex-col justify-between rounded-[20px] border-0 p-7 md:p-9",
                surfaces[index % surfaces.length],
                spans[item.columnSpan || 1],
              )}
              key={index}
              link={item.cardLink}
            >
              <div className="flex items-center gap-4">
                {item.portrait ? (
                  <StratosMedia
                    aspectRatio="1:1"
                    className="size-14 shrink-0"
                    media={item.portrait.image}
                    sizes="56px"
                  />
                ) : null}
                <div>
                  <p className="font-stratos-body text-sm font-medium">{item.name}</p>
                  {item.attribution ? (
                    <p
                      className={cn(
                        "mt-1 font-stratos-body text-xs",
                        index % surfaces.length === 0
                          ? "text-cloud-white/75"
                          : "text-night-flight/65",
                      )}
                    >
                      {item.attribution}
                    </p>
                  ) : null}
                </div>
              </div>
              <blockquote className="mt-10">
                <StratosRichText
                  className={cn(
                    "font-stratos-body text-2xl leading-snug md:text-3xl",
                    index % surfaces.length === 0 ? "text-cloud-white" : "text-night-flight",
                  )}
                  content={item.quote}
                />
              </blockquote>
            </StratosCardLink>
          ))}
        </div>
      </div>
    </StratosSection>
  );
};

TestimonialGridBlock.displayName = "Block::TestimonialGridBlock";
